import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import LocationsMap from "../elements/LocationsMap";
import useApi from "../../hooks/useApi";
import useLocalization from "../../hooks/useLocalization";

const LocationsMapModal = ({ open, onClose, locationIds = [], title }) => {
  const { getData } = useApi();
  const { getLabel, getMessage } = useLocalization();
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    if (open && locationIds.length > 0) {
      fetchLocations();
    }

    // Cleanup timeout on unmount
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [open, locationIds]);

  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const results = await getData({
        entityName: "Location",
        filter: {
          id: { $in: locationIds },
        },
      });

      if (results?.isSuccessful && Array.isArray(results?.data)) {
        setLocations(results.data);
      } else {
        setLocations([]);
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validLocations = locations.filter((loc) => {
    const hasLat =
      loc?.address?.latitude !== null && loc?.address?.latitude !== undefined;
    const hasLng =
      loc?.address?.longitude !== null && loc?.address?.longitude !== undefined;

    return hasLat && hasLng;
  });

  const mappedLocations = validLocations.map((loc) => ({
    ...loc,
    latitude: loc.address.latitude,
    longitude: loc.address.longitude,
  }));

  const modalTitle =
    title ||
    (locationIds.length === 1
      ? getLabel("mapLocation")
      : getLabel("mapSelected"));

  // Safe close handler with delay to allow Leaflet animations to finish
  const handleClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
    }, 150);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : mappedLocations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="textSecondary">
              {getMessage("noLocationsWithCoordinates")}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              {getMessage("mappingLocations")
                .replace("{count}", mappedLocations.length)
                .replace("{total}", locations.length)}
            </Typography>
            <Box sx={{ height: 600, width: "100%" }}>
              <LocationsMap
                locations={mappedLocations}
                mode="location"
                showTooltips={true}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{getLabel("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationsMapModal;
